import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import {
  GENDERS, HORSE_STATUSES, MEDICAL_CATEGORIES, REMINDER_CATEGORIES, REMINDER_INTERVALS,
  HORSE_SHARE_ROLES, MAX_SHARES_PER_HORSE,
  type Horse, type HorseVisibility, type SectionVisibility, type LocationVisibility,
  type HorseShareRole,
} from '@horsehub/shared'
import {
  useHorseShares, useCreateHorseShare, useRevokeHorseShare,
  useHorse, useUpdateHorse, useDeleteHorse, useSetVisibility,
  useMedical, useAddMedical, useDeleteMedical,
  useReminders, useAddReminder, useUpdateReminder, useDeleteReminder,
  useDocuments, useAddDocument, useDeleteDocument,
  useNews, useAddNews, useDeleteNews,
  useAddPhoto, useRemovePhoto,
} from '../api/hooks'
import { signAndUpload } from '../api/client'
import { Input, Textarea, Select, Modal, PageSpinner, Spinner } from '../components/ui'
import { ageFromDob, fmtDate } from '../lib/format'
import { confirmDelete } from '../lib/confirm'
import { toast } from '../lib/toast'
import { errorMessage } from '../lib/errors'

type Tab = 'info' | 'photos' | 'news' | 'medical' | 'reminders' | 'documents' | 'sharing'
const TABS: { key: Tab; icon: string; private?: boolean }[] = [
  { key: 'info', icon: 'ℹ️' },
  { key: 'photos', icon: '📷' },
  { key: 'news', icon: '📰' },
  { key: 'medical', icon: '🩺', private: true },
  { key: 'reminders', icon: '🔔', private: true },
  { key: 'documents', icon: '📄', private: true },
  { key: 'sharing', icon: '🔒' },
]

export function HorsePage() {
  const { id = '' } = useParams()
  const { t } = useI18n()
  const { data: horse, isLoading } = useHorse(id)
  const [tab, setTab] = useState<Tab>('info')

  if (isLoading) return <PageSpinner />
  if (!horse) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="text-2xl font-bold">{t('horse.notFound')}</h1>
        <Link to="/app" className="btn-primary mt-6">{t('horse.notFoundBack')}</Link>
      </main>
    )
  }

  return (
    <main className="mx-auto max-w-5xl px-5 py-8">
      <Link to="/app" className="text-sm font-medium text-brand-600 dark:text-brand-300 hover:underline">← {t('horse.back')}</Link>

      <div className="mt-4 overflow-hidden rounded-3xl shadow-lift">
        <div className="relative h-56 sm:h-72 bg-brand-100 dark:bg-brand-800">
          {horse.profilePhotoUrl && <img src={horse.profilePhotoUrl} alt={horse.name} className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 flex flex-wrap items-end justify-between gap-3 p-6 text-white">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-extrabold tracking-tight">{horse.name}</h1>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${horse.public ? 'bg-emerald-500/90' : 'bg-black/60'}`}>
                  {horse.public ? t('horse.public') : t('horse.private')}
                </span>
              </div>
              <p className="mt-1 text-white/85">❤ {horse.followerCount} {t('horse.followers')}</p>
            </div>
            {horse.public && (
              <Link to={`/h/${horse.id}`} className="rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-white">
                {t('horse.viewPublic')}
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
        {TABS.map((s) => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
              tab === s.key ? 'bg-brand-700 text-white shadow-soft' : 'bg-white/70 dark:bg-brand-800/60 text-brand-700 dark:text-brand-200 hover:bg-brand-100 dark:hover:bg-brand-800'
            }`}
          >
            <span>{s.icon}</span>
            {t(`horse.sections.${s.key}`)}
            {s.private && <span className="text-xs opacity-70">🔒</span>}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'info' && <InfoSection horse={horse} />}
        {tab === 'photos' && <PhotosSection horse={horse} />}
        {tab === 'news' && <NewsSection horse={horse} />}
        {tab === 'medical' && <MedicalSection horse={horse} />}
        {tab === 'reminders' && <RemindersSection horse={horse} />}
        {tab === 'documents' && <DocumentsSection horse={horse} />}
        {tab === 'sharing' && (
          <>
            <SharingSection horse={horse} />
            {!horse.sharedRole && <AccessPanel horse={horse} />}
          </>
        )}
      </div>
    </main>
  )
}

function SectionCard({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card p-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

// ── Info ────────────────────────────────────────────────────────────────────
function InfoSection({ horse }: { horse: Horse }) {
  const { t, td, locale } = useI18n()
  const [editing, setEditing] = useState(false)
  const age = ageFromDob(horse.dob)
  const rows: [string, string | undefined][] = [
    [t('horse.fields.dob'), horse.dob ? `${fmtDate(horse.dob, locale)}${age != null ? ` (${age} ${t('units.yrs')})` : ''}` : '—'],
    [t('horse.fields.breed'), horse.breed],
    [t('horse.fields.gender'), td('gender', horse.gender)],
    [t('horse.fields.colour'), horse.colour && td('colour', horse.colour)],
    [t('horse.fields.height'), horse.heightHands ? `${horse.heightHands} ${t('units.hh')}` : undefined],
    [t('horse.fields.discipline'), horse.discipline && td('discipline', horse.discipline)],
    [t('horse.fields.status'), td('status', horse.status)],
    [t('horse.fields.microchip'), horse.microchip],
    [t('horse.fields.passport'), horse.passport],
    [t('horse.fields.location'), [horse.city, horse.country].filter(Boolean).join(', ')],
  ]
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <SectionCard title={t('horse.about')} action={<button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => setEditing(true)}>{t('horse.edit')}</button>}>
        <p className="leading-relaxed text-brand-700 dark:text-brand-200">{horse.about || t('horse.noAbout')}</p>
      </SectionCard>
      <SectionCard title={t('horse.details')}>
        <dl className="grid gap-y-3 text-sm">
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-brand-100/70 dark:border-brand-700/50 pb-2">
              <dt className="text-brand-500 dark:text-brand-400">{k}</dt>
              <dd className="text-right font-medium capitalize">{v}</dd>
            </div>
          ))}
        </dl>
      </SectionCard>
      {editing && <EditHorseModal horse={horse} onClose={() => setEditing(false)} />}
    </div>
  )
}

function EditHorseModal({ horse, onClose }: { horse: Horse; onClose: () => void }) {
  const { t, td } = useI18n()
  const update = useUpdateHorse(horse.id)
  const del = useDeleteHorse()
  const [f, setF] = useState({
    name: horse.name, breed: horse.breed, gender: horse.gender, status: horse.status,
    dob: horse.dob || '', colour: horse.colour || '', heightHands: horse.heightHands?.toString() || '',
    discipline: horse.discipline || '', country: horse.country, city: horse.city || '',
    about: horse.about || '', microchip: horse.microchip || '', passport: horse.passport || '',
  })
  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v })

  const save = async () => {
    await update.mutateAsync({ ...f, heightHands: f.heightHands ? Number(f.heightHands) : undefined } as never)
    onClose()
  }
  const remove = async () => {
    if (!(await confirmDelete(t))) return
    await del.mutateAsync(horse.id)
    location.href = '/app'
  }

  return (
    <Modal title={t('horse.editHorse')} onClose={onClose}>
      <div className="space-y-4">
        <Input label={t('horse.fields.name')} value={f.name} onChange={set('name')} />
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.breed')} value={f.breed} onChange={set('breed')} />
          <Select label={t('horse.fields.gender')} value={f.gender} onChange={set('gender')} options={GENDERS.map((g) => ({ value: g, label: td('gender', g) }))} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.dob')} type="date" value={f.dob} onChange={set('dob')} />
          <Select label={t('horse.fields.status')} value={f.status} onChange={set('status')} options={HORSE_STATUSES.map((s) => ({ value: s, label: td('status', s) }))} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.colour')} value={f.colour} onChange={set('colour')} />
          <Input label={t('horse.fields.height')} value={f.heightHands} onChange={set('heightHands')} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.country')} value={f.country} onChange={set('country')} />
          <Input label={t('horse.fields.city')} value={f.city} onChange={set('city')} />
        </div>
        <Input label={t('horse.fields.discipline')} value={f.discipline} onChange={set('discipline')} />
        <Textarea label={t('horse.about')} value={f.about} onChange={set('about')} />
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.microchip')} value={f.microchip} onChange={set('microchip')} />
          <Input label={t('horse.fields.passport')} value={f.passport} onChange={set('passport')} />
        </div>
        <button onClick={save} disabled={update.isPending} className="btn-primary w-full disabled:opacity-60">{t('common.save')}</button>
        <button onClick={remove} className="w-full text-center text-sm text-red-600 hover:underline dark:text-red-400">{t('horse.deleteHorse')}</button>
      </div>
    </Modal>
  )
}

// ── Photos ──────────────────────────────────────────────────────────────────
function PhotosSection({ horse }: { horse: Horse }) {
  const { t } = useI18n()
  const addPhoto = useAddPhoto(horse.id)
  const removePhoto = useRemovePhoto(horse.id)
  const update = useUpdateHorse(horse.id)
  const [busy, setBusy] = useState(false)

  const onUpload = async (files: FileList | null) => {
    if (!files?.length) return
    setBusy(true)
    try {
      for (const file of Array.from(files)) {
        const { key } = await signAndUpload(horse.id, file, 'photos')
        await addPhoto.mutateAsync(key)
      }
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  const setAsProfile = async (file: File) => {
    setBusy(true)
    try {
      const { key } = await signAndUpload(horse.id, file, 'profile')
      await update.mutateAsync({ profilePhotoKey: key } as never)
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard
      title={t('horse.gallery')}
      action={
        <div className="flex gap-2">
          <label className="btn-ghost cursor-pointer !px-4 !py-2 text-xs">
            {t('horse.setProfilePhoto')}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && setAsProfile(e.target.files[0])} />
          </label>
          <label className="btn-primary cursor-pointer !px-4 !py-2 text-xs">
            {busy ? '…' : t('horse.upload')}
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} />
          </label>
        </div>
      }
    >
      {horse.galleryUrls && horse.galleryUrls.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {horse.galleryUrls.map((url, i) => (
            <div key={i} className="group relative aspect-square overflow-hidden rounded-2xl">
              <img src={url} alt="" className="h-full w-full object-cover" />
              {horse.galleryKeys?.[i] && (
                <button
                  aria-label={t('common.delete')}
                  onClick={() => confirmDelete(t).then((ok) => ok && removePhoto.mutate(horse.galleryKeys![i]))}
                  className="absolute right-2 top-2 grid h-10 w-10 place-items-center rounded-full bg-black/60 text-white transition focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('horse.noPhotos')}</p>
      )}
    </SectionCard>
  )
}

// ── News ────────────────────────────────────────────────────────────────────
function NewsSection({ horse }: { horse: Horse }) {
  const { t, locale } = useI18n()
  const { data: posts, isLoading } = useNews(horse.id)
  const add = useAddNews(horse.id)
  const del = useDeleteNews(horse.id)
  const [open, setOpen] = useState(false)

  const latest = posts?.[0]
  const rest = posts?.slice(1, 5) || []

  return (
    <SectionCard title={t('horse.newsTitle')} action={<button className="btn-primary !px-4 !py-2 text-xs" onClick={() => setOpen(true)}>{t('horse.addNews')}</button>}>
      {isLoading ? (
        <div className="grid place-items-center py-6"><Spinner /></div>
      ) : !posts?.length ? (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('horse.noNews')}</p>
      ) : (
        <div className="space-y-5">
          {latest && (
            <article className="overflow-hidden rounded-2xl border border-brand-100 dark:border-brand-700/60">
              {latest.imageUrls?.[0] && <img src={latest.imageUrls[0]} alt="" className="h-56 w-full object-cover" />}
              <div className="p-5">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-lg font-bold">{latest.title}</h3>
                  <button onClick={() => confirmDelete(t).then((ok) => ok && del.mutate(latest.id))} className="text-xs text-red-500 hover:underline">{t('common.delete')}</button>
                </div>
                <div className="text-xs text-brand-500 dark:text-brand-400">{fmtDate(latest.createdAt, locale)}</div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-brand-700 dark:text-brand-200">{latest.body}</p>
              </div>
            </article>
          )}
          {rest.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {rest.map((p) => (
                <article key={p.id} className="overflow-hidden rounded-2xl border border-brand-100 dark:border-brand-700/60">
                  {p.imageUrls?.[0] && <img src={p.imageUrls[0]} alt="" className="h-32 w-full object-cover" />}
                  <div className="p-4">
                    <div className="flex items-baseline justify-between gap-2">
                      <h4 className="font-semibold">{p.title}</h4>
                      <button aria-label={t('common.delete')} onClick={() => confirmDelete(t).then((ok) => ok && del.mutate(p.id))} className="text-xs text-red-500 hover:underline">✕</button>
                    </div>
                    <div className="text-xs text-brand-500 dark:text-brand-400">{fmtDate(p.createdAt, locale)}</div>
                    <p className="mt-1 line-clamp-2 text-sm text-brand-600 dark:text-brand-300">{p.body}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      )}
      {open && <AddNewsModal horseId={horse.id} add={add} onClose={() => setOpen(false)} />}
    </SectionCard>
  )
}

function AddNewsModal({ horseId, add, onClose }: { horseId: string; add: ReturnType<typeof useAddNews>; onClose: () => void }) {
  const { t } = useI18n()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      const keys: string[] = []
      for (const f of files.slice(0, 5)) {
        const { key } = await signAndUpload(horseId, f, 'news')
        keys.push(key)
      }
      await add.mutateAsync({ title, body, imageKeys: keys })
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={t('horse.addNews')} onClose={onClose}>
      <div className="space-y-4">
        <Input label={t('horse.newsTitleField')} value={title} onChange={setTitle} />
        <Textarea label={t('horse.newsBody')} value={body} onChange={setBody} rows={5} />
        <label className="btn-ghost cursor-pointer !py-2 text-sm">
          {t('horse.addImages')} {files.length > 0 && `(${files.length})`}
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </label>
        <button onClick={submit} disabled={busy || !title} className="btn-primary w-full disabled:opacity-60">{busy ? '…' : t('horse.publish')}</button>
      </div>
    </Modal>
  )
}

// ── Medical ─────────────────────────────────────────────────────────────────
function MedicalSection({ horse }: { horse: Horse }) {
  const { t, td, locale } = useI18n()
  const { data: records, isLoading } = useMedical(horse.id)
  const add = useAddMedical(horse.id)
  const del = useDeleteMedical(horse.id)
  const [open, setOpen] = useState(false)

  return (
    <SectionCard title={t('horse.medicalTitle')} action={<button className="btn-primary !px-4 !py-2 text-xs" onClick={() => setOpen(true)}>{t('horse.addRecord')}</button>}>
      {isLoading ? (
        <div className="grid place-items-center py-6"><Spinner /></div>
      ) : !records?.length ? (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('horse.noMedical')}</p>
      ) : (
        <ol className="relative space-y-5 border-l border-brand-200 dark:border-brand-700 pl-6">
          {records.map((r) => (
            <li key={r.id} className="relative">
              <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full bg-brand-600 ring-4 ring-brand-50 dark:ring-brand-900" />
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-bold">{td('medicalCategory', r.category)}{r.title ? ` — ${r.title}` : ''}</h3>
                <span className="text-xs text-brand-500 dark:text-brand-400">{fmtDate(r.date, locale)} · <button onClick={() => confirmDelete(t).then((ok) => ok && del.mutate(r.id))} className="text-red-500 hover:underline">{t('common.delete')}</button></span>
              </div>
              {r.notes && <p className="text-sm text-brand-700 dark:text-brand-200">{r.notes}</p>}
              {r.vet && <p className="mt-1 text-xs text-brand-500 dark:text-brand-400">{r.vet}</p>}
              {r.attachmentUrls && r.attachmentUrls.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.attachmentUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-brand-100 dark:bg-brand-800 px-2.5 py-1 text-xs font-medium text-brand-700 dark:text-brand-200 hover:underline">
                      📎 {t('horse.attachment')} {i + 1}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {open && <AddMedicalModal horseId={horse.id} add={add} onClose={() => setOpen(false)} />}
    </SectionCard>
  )
}

function AddMedicalModal({ horseId, add, onClose }: { horseId: string; add: ReturnType<typeof useAddMedical>; onClose: () => void }) {
  const { t, td } = useI18n()
  const [f, setF] = useState({ category: 'vaccination', title: '', date: new Date().toISOString().slice(0, 10), vet: '', notes: '' })
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v })

  const submit = async () => {
    if (busy || add.isPending) return // guard against a double-tap
    setBusy(true)
    try {
      const attachmentKeys: string[] = []
      for (const file of files.slice(0, 5)) {
        const { key } = await signAndUpload(horseId, file, 'medical')
        attachmentKeys.push(key)
      }
      await add.mutateAsync({ ...f, date: new Date(f.date).toISOString(), attachmentKeys } as never)
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={t('horse.addRecord')} onClose={onClose}>
      <div className="space-y-4">
        <Select label={t('horse.fields.category')} value={f.category} onChange={set('category')} options={MEDICAL_CATEGORIES.map((c) => ({ value: c, label: td('medicalCategory', c) }))} />
        <Input label={t('horse.fields.title')} value={f.title} onChange={set('title')} />
        <div className="grid grid-cols-2 gap-4">
          <Input label={t('horse.fields.date')} type="date" value={f.date} onChange={set('date')} />
          <Input label={t('horse.fields.vet')} value={f.vet} onChange={set('vet')} />
        </div>
        <Textarea label={t('horse.fields.notes')} value={f.notes} onChange={set('notes')} />
        <div>
          <label className="btn-ghost cursor-pointer !py-2 text-sm">
            📎 {t('horse.attachDocs')} {files.length > 0 && `(${files.length})`}
            <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </label>
          <p className="mt-1 text-xs text-brand-500 dark:text-brand-400">{t('horse.attachHint')}</p>
        </div>
        <button onClick={submit} disabled={busy || add.isPending} className="btn-primary w-full disabled:opacity-60">{busy ? '…' : t('common.save')}</button>
      </div>
    </Modal>
  )
}

// ── Reminders ───────────────────────────────────────────────────────────────
function RemindersSection({ horse }: { horse: Horse }) {
  const { t, td, locale } = useI18n()
  const { data: reminders, isLoading } = useReminders(horse.id)
  const add = useAddReminder(horse.id)
  const upd = useUpdateReminder(horse.id)
  const del = useDeleteReminder(horse.id)
  const [open, setOpen] = useState(false)

  return (
    <SectionCard title={t('horse.remindersTitle')} action={<button className="btn-primary !px-4 !py-2 text-xs" onClick={() => setOpen(true)}>{t('horse.addReminder')}</button>}>
      <p className="mb-4 text-xs text-brand-500 dark:text-brand-400">{t('horse.remindersHint')}</p>
      {isLoading ? (
        <div className="grid place-items-center py-6"><Spinner /></div>
      ) : !reminders?.length ? (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('horse.noReminders')}</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {reminders.map((r) => (
            <div key={r.id} className="rounded-2xl border border-brand-100 dark:border-brand-700/60 p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{td('medicalCategory', r.category)}{r.title ? ` — ${r.title}` : ''}</h3>
                <button aria-label={t('common.delete')} onClick={() => confirmDelete(t).then((ok) => ok && del.mutate(r.id))} className="text-xs text-red-500 hover:underline">✕</button>
              </div>
              <p className="mt-2 text-sm font-medium">{t('horse.next')} {fmtDate(r.nextDue, locale)}</p>
              <p className="text-xs text-brand-500 dark:text-brand-400">{t('horse.everyMonths', { n: r.intervalMonths })}</p>
              <button onClick={() => upd.mutate({ rid: r.id, body: { markDone: true } })} className="btn-ghost mt-3 w-full !py-1.5 text-xs">{t('horse.markDone')}</button>
            </div>
          ))}
        </div>
      )}
      {open && <AddReminderModal add={add} onClose={() => setOpen(false)} />}
    </SectionCard>
  )
}

function AddReminderModal({ add, onClose }: { add: ReturnType<typeof useAddReminder>; onClose: () => void }) {
  const { t, td } = useI18n()
  const [f, setF] = useState({ category: 'farrier', title: '', intervalMonths: '2', nextDue: new Date().toISOString().slice(0, 10) })
  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v })

  // Guard against a double-tap creating two reminders.
  const submit = async () => {
    if (add.isPending) return
    try {
      await add.mutateAsync({ category: f.category, title: f.title, intervalMonths: Number(f.intervalMonths), nextDue: new Date(f.nextDue).toISOString() } as never)
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <Modal title={t('horse.addReminder')} onClose={onClose}>
      <div className="space-y-4">
        <Select label={t('horse.fields.category')} value={f.category} onChange={set('category')} options={REMINDER_CATEGORIES.map((c) => ({ value: c, label: td('medicalCategory', c) }))} />
        <Input label={t('horse.fields.title')} value={f.title} onChange={set('title')} />
        <div className="grid grid-cols-2 gap-4">
          <Select label={t('horse.fields.interval')} value={f.intervalMonths} onChange={set('intervalMonths')} options={REMINDER_INTERVALS.map((n) => ({ value: String(n), label: t('horse.everyMonths', { n }) }))} />
          <Input label={t('horse.fields.nextDue')} type="date" value={f.nextDue} onChange={set('nextDue')} />
        </div>
        <button onClick={submit} disabled={add.isPending} className="btn-primary w-full disabled:opacity-60">{add.isPending ? '…' : t('common.save')}</button>
      </div>
    </Modal>
  )
}

// ── Documents ───────────────────────────────────────────────────────────────
function DocumentsSection({ horse }: { horse: Horse }) {
  const { t, locale } = useI18n()
  const { data: docs, isLoading } = useDocuments(horse.id)
  const add = useAddDocument(horse.id)
  const del = useDeleteDocument(horse.id)
  const [busy, setBusy] = useState(false)

  const onUpload = async (file: File | undefined) => {
    if (!file) return
    setBusy(true)
    try {
      const up = await signAndUpload(horse.id, file, 'documents')
      await add.mutateAsync({ key: up.key, name: up.name })
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard
      title={t('horse.documentsTitle')}
      action={
        <label className="btn-primary cursor-pointer !px-4 !py-2 text-xs">
          {busy ? '…' : t('horse.upload')}
          <input type="file" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
        </label>
      }
    >
      {isLoading ? (
        <div className="grid place-items-center py-6"><Spinner /></div>
      ) : !docs?.length ? (
        <p className="text-sm text-brand-600 dark:text-brand-300">{t('horse.noDocuments')}</p>
      ) : (
        <ul className="divide-y divide-brand-100 dark:divide-brand-700/60">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-100 dark:bg-brand-800">{d.kind === 'pdf' ? '📕' : d.kind === 'image' ? '🖼️' : '📄'}</span>
                <div>
                  <div className="font-semibold">{d.name}</div>
                  <div className="text-xs text-brand-500 dark:text-brand-400">{fmtDate(d.uploadedAt, locale)}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-300">{t('horse.download')}</a>}
                <button aria-label={t('common.delete')} onClick={() => confirmDelete(t).then((ok) => ok && del.mutate(d.id))} className="text-xs text-red-500 hover:underline">✕</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

// ── Sharing / visibility ─────────────────────────────────────────────────────
function SharingSection({ horse }: { horse: Horse }) {
  const { t } = useI18n()
  const setVis = useSetVisibility(horse.id)
  const v = horse.visibility

  const sections: (keyof HorseVisibility)[] = ['info', 'photos', 'news', 'pedigree']
  const setSection = (key: keyof HorseVisibility, val: SectionVisibility | LocationVisibility) =>
    setVis.mutate({ visibility: { ...v, [key]: val } })

  return (
    <SectionCard title={t('horse.sharingTitle')}>
      <label className="flex items-center justify-between rounded-2xl bg-brand-100/50 dark:bg-brand-800/40 px-4 py-3">
        <div>
          <div className="font-semibold">{t('horse.makePublic')}</div>
          <div className="text-xs text-brand-500 dark:text-brand-400">{t('horse.makePublicHint')}</div>
        </div>
        <input type="checkbox" checked={horse.public} onChange={(e) => setVis.mutate({ public: e.target.checked })} className="h-6 w-11 cursor-pointer appearance-none rounded-full bg-brand-300 transition checked:bg-brand-600 relative before:absolute before:top-0.5 before:left-0.5 before:h-5 before:w-5 before:rounded-full before:bg-white before:transition checked:before:translate-x-5" />
      </label>

      <p className="mb-2 mt-6 text-sm font-semibold">{t('horse.whatPublic')}</p>
      <div className="space-y-2">
        {sections.map((key) => (
          <div key={key} className="flex items-center justify-between gap-4 rounded-xl border border-brand-100 dark:border-brand-700/60 px-4 py-2.5">
            <span className="text-sm">{t(`horse.sections.${key}`)}</span>
            <select
              value={v[key]}
              onChange={(e) => setSection(key, e.target.value as SectionVisibility)}
              className="rounded-lg border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-1.5 text-sm"
            >
              <option value="public">{t('horse.vis.public')}</option>
              <option value="private">{t('horse.vis.private')}</option>
            </select>
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 rounded-xl border border-brand-100 dark:border-brand-700/60 px-4 py-2.5">
          <span className="text-sm">{t('horse.fields.location')}</span>
          <select
            value={v.location}
            onChange={(e) => setSection('location', e.target.value as LocationVisibility)}
            className="rounded-lg border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-1.5 text-sm"
          >
            <option value="country">{t('horse.vis.country')}</option>
            <option value="city">{t('horse.vis.city')}</option>
            <option value="private">{t('horse.vis.private')}</option>
          </select>
        </div>
      </div>
      <p className="mt-4 text-xs text-brand-500 dark:text-brand-400">{t('horse.alwaysPrivate')}</p>
    </SectionCard>
  )
}


/**
 * People the owner has given access to THIS horse. Owner-only: someone holding
 * a share can see the horse but never manage who else can.
 */
function AccessPanel({ horse }: { horse: Horse }) {
  const { t } = useI18n()
  const { data, isLoading } = useHorseShares(horse.id)
  const create = useCreateHorseShare(horse.id)
  const revoke = useRevokeHorseShare(horse.id)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<HorseShareRole>('co-owner')

  const shares = data?.shares ?? []
  const invites = data?.invites ?? []
  const full = shares.length + invites.length >= MAX_SHARES_PER_HORSE

  const submit = async () => {
    if (!email.trim()) return
    await create.mutateAsync({ email: email.trim(), role })
    setEmail('')
  }

  return (
    <SectionCard title={t('horse.access.title')}>
      <p className="mb-4 text-sm text-brand-600 dark:text-brand-300">{t('horse.access.body')}</p>

      {isLoading ? (
        <div className="grid place-items-center py-4"><Spinner /></div>
      ) : (
        <div className="space-y-2">
          {shares.map((s) => (
            <div key={s.userId} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-brand-100 dark:border-brand-700/60 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{s.name}</div>
                <div className="truncate text-xs text-brand-500 dark:text-brand-400">{s.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="chip !px-2 !py-0.5 text-xs">{t(`horse.access.role.${s.role === 'co-owner' ? 'coOwner' : 'viewer'}`)}</span>
                <button onClick={() => revoke.mutate(s.userId)} disabled={revoke.isPending}
                  className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-60">
                  {t('horse.access.remove')}
                </button>
              </div>
            </div>
          ))}
          {invites.map((i) => (
            <div key={i.token} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed border-brand-200 dark:border-brand-700 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm">{i.email}</div>
                <div className="text-xs text-brand-500 dark:text-brand-400">{t('horse.access.pending')}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="chip !px-2 !py-0.5 text-xs">{t(`horse.access.role.${i.role === 'co-owner' ? 'coOwner' : 'viewer'}`)}</span>
                <button onClick={() => revoke.mutate(i.token)} disabled={revoke.isPending}
                  className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-60">
                  {t('horse.access.cancel')}
                </button>
              </div>
            </div>
          ))}
          {shares.length === 0 && invites.length === 0 && (
            <p className="text-sm text-brand-500 dark:text-brand-400">{t('horse.access.none')}</p>
          )}
        </div>
      )}

      {!full && (
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Input label={t('horse.access.email')} type="email" value={email} onChange={setEmail}
            placeholder={t('horse.access.emailPlaceholder')} />
          <Select label={t('horse.access.roleLabel')} value={role} onChange={(v) => setRole(v as HorseShareRole)}
            options={HORSE_SHARE_ROLES.map((r) => ({
              value: r, label: t(`horse.access.role.${r === 'co-owner' ? 'coOwner' : 'viewer'}`),
            }))} />
          <button onClick={submit} disabled={create.isPending || !email.trim()}
            className="btn-primary !py-2.5 disabled:opacity-60">
            {create.isPending ? t('horse.access.inviting') : t('horse.access.invite')}
          </button>
        </div>
      )}
      {full && <p className="mt-3 text-xs text-brand-500">{t('horse.access.full', { n: MAX_SHARES_PER_HORSE })}</p>}
      <p className="mt-3 text-xs text-brand-500 dark:text-brand-400">{t('horse.access.note')}</p>
    </SectionCard>
  )
}
